import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, ArrowRight, FileText } from 'lucide-react';
import AdBanner from '../components/Layout/AdBanner';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  // Mock blog post data - in a real app, this would come from an API or CMS
  const post = {
    title: 'What is an invoice and what is it for?',
    publishedAt: '2024-01-15',
    readTime: 5,
    category: 'Basics',
    content: `
      <h2>Understanding Invoices: The Foundation of Business Transactions</h2>
      
      <p>An invoice is a commercial document that itemizes and records a transaction between a buyer and a seller. It serves as a formal request for payment and provides important details about the goods or services provided.</p>

      <h3>Key Components of an Invoice</h3>
      
      <p>Every professional invoice should include the following essential elements:</p>
      
      <ul>
        <li><strong>Invoice number:</strong> A unique identifier for tracking purposes</li>
        <li><strong>Date:</strong> When the invoice was created</li>
        <li><strong>Due date:</strong> When payment is expected</li>
        <li><strong>Seller information:</strong> Your business name, address, and contact details</li>
        <li><strong>Buyer information:</strong> Client's name, address, and contact details</li>
        <li><strong>Description of goods/services:</strong> Detailed list of what was provided</li>
        <li><strong>Quantities and rates:</strong> How much of each item and at what price</li>
        <li><strong>Total amount:</strong> The final amount due including any taxes</li>
      </ul>

      <h3>Why Invoices Matter for Your Business</h3>

      <p>Invoices serve multiple crucial purposes in business operations:</p>

      <h4>1. Legal Protection</h4>
      <p>Invoices serve as legal documents that prove a transaction took place. They protect both buyers and sellers in case of disputes and provide evidence of the agreed-upon terms.</p>

      <h4>2. Cash Flow Management</h4>
      <p>Proper invoicing helps you track outstanding payments and manage your cash flow effectively. This is essential for maintaining healthy business operations.</p>

      <h4>3. Tax Compliance</h4>
      <p>Invoices are required for tax purposes, both for claiming business expenses and reporting income. They provide the documentation needed for accurate tax filing.</p>

      <h4>4. Professional Image</h4>
      <p>Well-designed, professional invoices enhance your business credibility and help build trust with clients.</p>

      <h3>Types of Invoices</h3>

      <p>Different situations call for different types of invoices:</p>

      <ul>
        <li><strong>Standard Invoice:</strong> Used for one-time transactions</li>
        <li><strong>Recurring Invoice:</strong> For ongoing services billed regularly</li>
        <li><strong>Proforma Invoice:</strong> A preliminary bill sent before work begins</li>
        <li><strong>Credit Invoice:</strong> Used to correct errors or provide refunds</li>
        <li><strong>Timesheet Invoice:</strong> Based on hours worked</li>
      </ul>

      <h3>Best Practices for Invoice Management</h3>

      <p>To ensure smooth payment processing and maintain good client relationships:</p>

      <ul>
        <li>Send invoices promptly after work completion</li>
        <li>Use clear, concise descriptions</li>
        <li>Include payment terms and methods</li>
        <li>Follow up on overdue payments professionally</li>
        <li>Keep detailed records of all invoices</li>
        <li>Use professional invoice templates</li>
      </ul>

      <h3>Creating Professional Invoices</h3>

      <p>With modern invoice generators like InvoiceGen, creating professional invoices has never been easier. You can generate PDF invoices in minutes with all the necessary fields and professional formatting.</p>

      <p>Whether you're a freelancer, small business owner, or part of a larger organization, understanding invoices and implementing proper invoicing practices is essential for business success.</p>
    `
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-8 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Navigation */}
          <div className="mb-8">
            <Link
              to="/blog"
              className="text-blue-600 hover:text-blue-700 font-medium flex items-center space-x-2 transition-colors duration-200"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Blog</span>
            </Link>
          </div>

          {/* Article Header */}
          <article className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-8 md:p-12">
              <div className="mb-6">
                <div className="flex items-center space-x-4 mb-4">
                  <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded">
                    {post.category}
                  </span>
                  <div className="flex items-center text-gray-500 text-sm">
                    <Calendar className="h-4 w-4 mr-1" />
                    {new Date(post.publishedAt).toLocaleDateString()}
                  </div>
                  <div className="flex items-center text-gray-500 text-sm">
                    <Clock className="h-4 w-4 mr-1" />
                    {post.readTime} min read
                  </div>
                </div>

                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {post.title}
                </h1>
              </div>

              {/* Ad Banner */}
              <div className="mb-8">
                <AdBanner size="responsive" />
              </div>

              {/* Article Content */}
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Mid-content Ad */}
              <div className="my-12">
                <AdBanner size="responsive" />
              </div>

              {/* CTA Section */}
              <div className="mt-12 p-8 bg-blue-50 rounded-xl border border-blue-200">
                <div className="text-center">
                  <FileText className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Ready to Create Your Invoice?
                  </h3>
                  <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                    Put what you've learned into practice. Use our free invoice generator to create professional invoices in minutes.
                  </p>
                  <Link
                    to="/generate-invoice"
                    className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 inline-flex items-center space-x-2"
                  >
                    <span>Generate Invoice Now</span>
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </div>
              </div>
            </div>
          </article>

          {/* Related Posts */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link
                to="/blog/invoices-for-self-employed"
                className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">How to issue invoices as a freelancer</h4>
                <p className="text-gray-600 text-sm">A complete guide for freelancers on creating and managing invoices professionally.</p>
              </Link>

              <Link
                to="/blog/good-practices-invoice"
                className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Best practices when creating an invoice</h4>
                <p className="text-gray-600 text-sm">Essential best practices to create professional and effective invoices.</p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;